/**
 * Service Worker registration with update notifications.
 * Uses vite-plugin-pwa's registerSW for automatic registration and update handling.
 * The actual SW file is generated by vite-plugin-pwa during build with Workbox strategies:
 * - CacheFirst for static assets (JS, CSS, images) - max 60 entries, 30-day expiry
 * - StaleWhileRevalidate for catalog data (/api/sets/search, /api/inventory)
 * - NetworkFirst for general API calls (/api/) - 10s timeout, offline fallback
 */

let updateSWCallback: ((reloadPage?: boolean) => Promise<void>) | undefined;

/**
 * Registers the Service Worker and sets up update detection.
 * When a new version is available, it will notify the user via the
 * onUpdateAvailable callback (if set) and can be applied by calling applyUpdate().
 */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        console.log("SW registered:", registration.scope);

        // Check for updates periodically (every 60 minutes)
        setInterval(
          () => {
            registration.update();
          },
          60 * 60 * 1000,
        );

        // Listen for new service worker waiting to activate
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // New version available - notify the app
              notifyUpdateAvailable(registration);
            }
          });
        });
      })
      .catch((error) => {
        console.log("SW registration failed:", error);
      });
  });

  // Handle controller change (new SW activated) - reload to get new assets
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (updateSWCallback) {
      window.location.reload();
    }
  });
}

/** Callback type for update notifications */
export type UpdateAvailableCallback = () => void;

let onUpdateAvailableCallback: UpdateAvailableCallback | undefined;

/**
 * Set a callback to be invoked when a new version of the app is available.
 * The UI can use this to show a "New version available" banner.
 */
export function onUpdateAvailable(callback: UpdateAvailableCallback): void {
  onUpdateAvailableCallback = callback;
}

/**
 * Apply a pending update by activating the waiting Service Worker.
 * This will trigger a page reload via the controllerchange listener.
 */
export function applyUpdate(): void {
  if (updateSWCallback) {
    updateSWCallback(true);
  }
}

function notifyUpdateAvailable(registration: ServiceWorkerRegistration): void {
  console.log("New app version available");

  // Store the skip-waiting trigger for later use
  updateSWCallback = async () => {
    const waiting = registration.waiting;
    if (waiting) {
      waiting.postMessage({ type: "SKIP_WAITING" });
    }
  };

  // Notify the UI
  if (onUpdateAvailableCallback) {
    onUpdateAvailableCallback();
  }
}
